import React, { useState } from 'react';
import { FaMoneyBillWave, FaMobileAlt, FaCreditCard, FaUniversity, FaWallet, FaLock, FaSpinner, FaExclamationCircle } from 'react-icons/fa';
import { formatCurrency } from '../../../utils/cartTotals';

const PAYMENT_METHODS = [
  {
    id: 'COD',
    label: 'Cash on Delivery',
    description: 'Pay when your order arrives',
    icon: FaMoneyBillWave,
  },
  {
    id: 'UPI',
    label: 'UPI',
    description: 'Pay via GPay, PhonePe, Paytm & more',
    icon: FaMobileAlt,
  },
  {
    id: 'CARD',
    label: 'Credit / Debit Card',
    description: 'Visa, Mastercard, RuPay & more',
    icon: FaCreditCard,
  },
  {
    id: 'NETBANKING',
    label: 'Net Banking',
    description: 'All major banks supported',
    icon: FaUniversity,
  },
  {
    id: 'WALLET',
    label: 'Wallet',
    description: 'Paytm, Amazon Pay & others',
    icon: FaWallet,
  },
];

const PaymentStep = ({
  total,
  paymentMethod,
  onMethodChange,
  onPlaceOrder,
  placing,
  orderError,
}) => {
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onPlaceOrder(notes);
  };

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-black/5 sm:p-8">
      <h2 className="mb-6 flex items-center gap-3 text-xl font-extrabold text-gray-900">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-black text-primary-600">
          3
        </span>
        Payment
      </h2>

      {/* Payment method selection */}
      <div className="mb-6 space-y-3">
        <p className="mb-3 text-sm font-semibold text-gray-500 uppercase tracking-widest">
          Select Payment Method
        </p>
        {PAYMENT_METHODS.map(({ id, label, description, icon: Icon }) => {
          const isSelected = paymentMethod === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onMethodChange(id)}
              className={`flex w-full items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all duration-200 ${
                isSelected
                  ? 'border-primary-500 bg-primary-50 shadow-md shadow-primary-500/10'
                  : 'border-gray-100 bg-gray-50 hover:border-primary-200 hover:bg-primary-50/50'
              }`}
            >
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors ${
                  isSelected ? 'bg-primary-500 text-white' : 'bg-white text-gray-400'
                }`}
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block font-bold ${isSelected ? 'text-primary-700' : 'text-gray-800'}`}>
                  {label}
                </span>
                <span className="block text-xs text-gray-500">{description}</span>
              </span>
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                  isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                }`}
              >
                {isSelected && <span className="h-2 w-2 rounded-full bg-white" />}
              </span>
            </button>
          );
        })}
      </div>

      {/* Order notes */}
      <div className="mb-6">
        <label htmlFor="order-notes" className="mb-2 block text-sm font-semibold text-gray-700">
          Order Notes <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          id="order-notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any special instructions for your order..."
          className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100 transition-all"
        />
      </div>

      {/* Error message */}
      {orderError && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3">
          <FaExclamationCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm font-medium text-red-700">{orderError}</p>
        </div>
      )}

      {/* Order total summary */}
      <div className="mb-6 flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50 px-5 py-4">
        <span className="text-sm font-semibold text-gray-500">Total Payable</span>
        <span className="text-xl font-extrabold text-gray-900">{formatCurrency(total)}</span>
      </div>

      {/* Place order button */}
      <form onSubmit={handleSubmit}>
        <button
          type="submit"
          disabled={placing}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-600 py-4 text-base font-bold text-white shadow-lg shadow-primary-600/30 transition-all hover:-translate-y-0.5 hover:bg-primary-700 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-lg"
        >
          {placing ? (
            <>
              <FaSpinner className="h-4 w-4 animate-spin" />
              Placing Order…
            </>
          ) : (
            <>
              <FaLock className="h-4 w-4" />
              {paymentMethod === 'COD' ? 'Place Order' : `Pay ${formatCurrency(total)}`}
            </>
          )}
        </button>
      </form>

      <p className="mt-4 text-center text-xs text-gray-400">
        🔒 Your payment information is secured and encrypted
      </p>
    </div>
  );
};

export default PaymentStep;
